import type { ToolSpec, ToolValues, ToolCard } from "./types";
import { defaults } from "./types";

/**
 * Deterministic, page-specific expansion helpers.
 *
 * Goal: cover conversational / long-tail phrasing the way Google recommends —
 * with real questions that get real answers, and with worked examples that
 * produce genuinely different numbers on every page. No keyword lists,
 * no misspelling dumps, no hidden text.
 */

export interface QuickAnswer {
  q: string;
  a: string;
}

const list = (xs: string[]): string =>
  xs.length <= 1 ? xs[0] ?? "" : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;

const lower = (x: string) => x.charAt(0).toLowerCase() + x.slice(1);

const firstSentence = (text: string): string => {
  const m = text.match(/[^.]+\./);
  return (m ? m[0] : text).trim();
};

/** 8-12 conversational Q&As built from this tool's own fields, table and copy. */
export function quickAnswers(spec: ToolSpec): QuickAnswer[] {
  const inputLabels = spec.inputs.map((f) => lower(f.label.replace(/\s*\(optional\)/i, "")));
  const required = spec.inputs.filter((f) => !/optional/i.test(f.label));
  const optional = spec.inputs.filter((f) => /optional/i.test(f.label));
  const selects = spec.inputs.filter((f) => f.type === "select");
  const unitField = spec.inputs.find((f) => /unit|system|metric|currency/i.test(f.label));
  const table = spec.staticTable;
  const firstRow = table.rows[0] ?? [];
  const lastRow = table.rows[table.rows.length - 1] ?? [];
  const method = spec.sections[0];
  const secondary = spec.sections[1];
  const out: QuickAnswer[] = [];

  out.push({
    q: `What exactly does the ${spec.name} work out?`,
    a: `${firstSentence(spec.intro)} You enter ${list(inputLabels.slice(0, 4))}${
      inputLabels.length > 4 ? ` (plus ${inputLabels.length - 4} more optional details)` : ""
    } and the result panel updates straight away, so you can compare two or three versions of the same question in a few seconds.`,
  });

  out.push({
    q: `What do I need before I start?`,
    a: `Only ${required.length} field${required.length === 1 ? "" : "s"}: ${list(
      required.map((f) => lower(f.label)),
    )}.${
      optional.length
        ? ` ${list(optional.map((f) => lower(f.label.replace(/\s*\(optional\)/i, ""))))} ${
            optional.length === 1 ? "is" : "are"
          } optional — leaving ${optional.length === 1 ? "it" : "them"} blank simply switches the tool to the simpler estimate.`
        : " Nothing else is needed and nothing is stored."
    }`,
  });

  if (method) {
    out.push({
      q: `${/^how|^what|\?$/i.test(method.h) ? method.h.replace(/\?$/, "") : `How is it calculated — ${lower(method.h)}`}?`,
      a: firstSentence(method.body) + " The same maths runs inside this page, so hand-checking the result on paper gives you the identical figure.",
    });
  }

  if (secondary) {
    out.push({
      q: `Why do two calculators give me different answers for ${lower(spec.name.replace(/ calculator| converter| estimator/i, ""))}?`,
      a: `${firstSentence(secondary.body)} Different sites pick different assumptions, so always check which method a calculator states before you trust the gap between two numbers.`,
    });
  }

  if (table.rows.length && firstRow.length >= 2) {
    out.push({
      q: `What does the "${table.title}" table on this page tell me?`,
      a: `It is the reference range this tool works against — ${table.rows.length} rows from "${firstRow[0]}" (${firstRow[1]}) up to "${lastRow[0]}" (${lastRow[1]}). Use it to sanity-check whether the number you just calculated sits where you expected it to.${
        table.caption ? ` ${table.caption}` : ""
      }`,
    });
  }

  if (selects.length) {
    const sel = selects[0];
    const opts = (sel.options ?? []).map((o) => o.label.split("—")[0].trim());
    out.push({
      q: `Which ${lower(sel.label)} should I pick?`,
      a: `The dropdown offers ${opts.length} choices — ${list(opts)}. Pick the one that matches your real situation rather than the one you would like to be true; ${lower(
        sel.label,
      )} usually moves the final figure more than any other single input, so it is worth running it twice with the option above and below your guess.`,
    });
  }

  if (unitField) {
    out.push({
      q: `Does it work in both metric and imperial (or another currency)?`,
      a: `Yes. The "${unitField.label}" control converts every field and every result, so you never have to convert anything by hand before typing it in. Switch it after entering your numbers and the output re-renders instantly in the new system.`,
    });
  }

  out.push({
    q: `Do I have to press a button or reload the page to see the result?`,
    a: `No. ${spec.name} runs completely inside your browser, so the moment you change a value the cards recalculate — there is no submit step, no page reload and no waiting for a server round trip. That also means it keeps working on a weak or intermittent mobile connection.`,
  });

  out.push({
    q: `Is it free, and do you keep what I type?`,
    a: `It is free with no sign-up, no app install and no usage limit. Nothing you enter into ${spec.name} leaves your device — the calculation is JavaScript running locally, so there is no upload of your figures to DrHint or anyone else.`,
  });

  out.push({
    q: `Can I use it on a phone?`,
    a: `Yes — the layout stacks to a single column on small screens and the number fields open the numeric keypad on both Android and iOS. Many people bookmark this page or add it to their home screen and re-open it whenever the question comes up.`,
  });

  if (spec.faqs.length) {
    out.push({
      q: `Anything to be careful about with the result?`,
      a: `${firstSentence(spec.faqs[0].a)} Treat the output as a well-grounded estimate for planning, not as a professional, legal or medical decision on its own.`,
    });
  }

  return out;
}

/* ------------------------------ scenarios ------------------------------ */

export interface ToolScenario {
  label: string;
  note: string;
  cards: ToolCard[];
}

function variantValues(spec: ToolSpec, factor: number, selectShift: number): ToolValues {
  const v = { ...defaults(spec) };
  const numeric = spec.inputs.filter(
    (f) => f.type === "number" && v[f.id] !== "" && Number.isFinite(Number(v[f.id])),
  );
  const primary = numeric[0];
  if (primary) {
    const base = Number(v[primary.id]);
    let next = base * factor;
    if (primary.min != null) next = Math.max(primary.min, next);
    if (primary.max != null) next = Math.min(primary.max, next);
    v[primary.id] = String(Math.round(next * 100) / 100);
  }
  const sel = spec.inputs.find((f) => f.type === "select" && (f.options?.length ?? 0) > 1);
  if (sel && selectShift !== 0) {
    const opts = sel.options!;
    const idx = Math.max(0, opts.findIndex((o) => o.value === v[sel.id]));
    v[sel.id] = opts[Math.min(opts.length - 1, Math.max(0, idx + selectShift))].value;
  }
  return v;
}

/** Three worked examples computed with this tool's own engine — unique numbers per page. */
export function buildScenarios(spec: ToolSpec): ToolScenario[] {
  const recipes: { factor: number; shift: number; tone: string }[] = [
    { factor: 0.78, shift: -1, tone: "On the lower / more conservative end" },
    { factor: 1, shift: 0, tone: "A typical middle-of-the-road setup" },
    { factor: 1.3, shift: 1, tone: "On the higher / more demanding end" },
  ];
  const primary = spec.inputs.find((f) => f.type === "number");
  const sel = spec.inputs.find((f) => f.type === "select" && (f.options?.length ?? 0) > 1);

  return recipes
    .map((r) => {
      const values = variantValues(spec, r.factor, r.shift);
      let out;
      try {
        out = spec.compute(values);
      } catch {
        return null;
      }
      if (!out.cards.length) return null;
      const bits: string[] = [];
      if (primary) bits.push(`${primary.label.toLowerCase()} ${values[primary.id]}${primary.unit ? ` ${primary.unit}` : ""}`);
      if (sel) {
        const label = (sel.options ?? []).find((o) => o.value === values[sel.id])?.label ?? "";
        if (label) bits.push(`${sel.label.toLowerCase()} "${label.split("—")[0].trim()}"`);
      }
      return {
        label: bits.length ? bits.join(", ") : r.tone,
        note: `${r.tone}. ${out.summary ?? `Change any field above to see how far this moves.`}`,
        cards: out.cards.slice(0, 4),
      } as ToolScenario;
    })
    .filter(Boolean) as ToolScenario[];
}
